<template>
  <v-card :data-cy="`${header.text}-filter-menu`">
    <v-list
      v-if="needsMenu"
      class="sn-table-column-menu"
      subheader
    >
      <template
        v-if="showSort"
      >
        <v-subheader class="sn-subtitle-2 sn-table-filter-subheader">
          SORT
        </v-subheader>
        <v-list-item-group
          v-model="selectedSort"
        >
          <v-list-item
            v-for="sort in header.sortBy"
            :key="sort.value"
            :value="sort.value"
            :ripple="false"
            class="sn-table-filter-menu-item"
            :data-cy="`sort-option-${sort.text}`"
          >
            <template v-slot:default="{ active }">
              <v-list-item-action class="mr-3">
                <v-icon
                  color="snBrand03"
                  v-text="active ? '$snRadioSelected' : '$snRadio'"
                />
              </v-list-item-action>

              <v-list-item-content>
                <v-list-item-title
                  class="sn-body-1"
                  color="snBrand03"
                >
                  {{ sort.text }}
                </v-list-item-title>
              </v-list-item-content>
            </template>
          </v-list-item>
        </v-list-item-group>
      </template>

      <template v-if="showFilters">
        <v-row>
          <v-subheader class="sn-subtitle-2 sn-table-filter-subheader ml-4 mr-10">
            FILTER
          </v-subheader>
          <sn-btn-deprecated
            v-if="selectedFilters.length && showClearAll"
            class="ml-10 mt-3"
            type="text"
            size="large"
            text="Clear all"
            data-cy="clear-all-btn"
            @click="clearFilters"
          />
        </v-row>
        <v-row
          v-if="sn_snTableFilterMixin_hasSearch"
          class="justify-center"
        >
          <sn-search
            width="218px"
            :value="searchOptions.text"
            @search="applySearchOnFilter"
          />
        </v-row>
        <v-list-item-group
          v-model="selectedFilters"
          class="sn-table-filter-menu"
          :multiple="allowMultiSelect"
        >
          <v-list-item
            v-for="filter in filteredHeader.nodes"
            :key="filter.value"
            :value="filter.value"
            :ripple="false"
            class="sn-table-filter-menu-item"
            :data-cy="`filter-option-${filter.text}`"
          >
            <template v-slot:default="{ active }">
              <v-list-item-action class="mr-3">
                <v-icon
                  color="snBrand03"
                  v-text="active ? '$snCheckBoxSelected' : '$snCheckBox'"
                />
              </v-list-item-action>

              <v-list-item-content>
                <v-list-item-title
                  class="sn-body-1"
                  color="snBrand03"
                >
                  {{ filter.text }}
                </v-list-item-title>
              </v-list-item-content>
            </template>
          </v-list-item>
          <sn-infinite-loader
            :has-next-page="hasNextPage"
            @load-more="$emit('load-more-filters')"
          />
        </v-list-item-group>
      </template>
    </v-list>
    <v-card-actions class="pa-0">
      <v-spacer />
      <sn-btn-deprecated
        v-if="needsMenu"
        size="standard"
        text="Apply"
        class="sn-table-column-menu-btn"
        data-cy="apply-btn"
        @click="applyChanges"
      />
    </v-card-actions>
  </v-card>
</template>

<script>

import SnBtnDeprecated from '~/components/snui/v2/_deprecated/snBtnDeprecated/snBtnDeprecated'
import SnSearch from '~/components/snui/v2/snSearch/snSearch'
import snTableFilterMixin from './snTableFilterMixin'
import SnInfiniteLoader from '~/components/snui/v2/snInfiniteLoader/snInfiniteLoader.vue'

export default {
  name: 'SnTableFilterMenu',
  components: {
    SnInfiniteLoader,
    SnSearch,
    SnBtnDeprecated
  },
  mixins: [snTableFilterMixin],
  props: {
    hideSort: {
      type: Boolean,
      required: false,
      default: false
    },
    hideFilters: {
      type: Boolean,
      required: false,
      default: false
    },
    showClearAll: {
      type: Boolean,
      required: false,
      default: false
    },
    searchOptions: {
      type: Object,
      required: false,
      default: () => {
        return {
          label: 'Search',
          text: ''
        }
      }
    }
  },
  data () {
    return {
      selectedSort: null,
      selectedFilters: null,
      menuOpen: false,
      filteredHeader: []
    }
  },
  computed: {
    showSort () {
      return this.header.sortBy && !this.hideSort
    },
    showFilters () {
      return this.header.filterBy && !this.hideFilters
    },
    allowMultiSelect () {
      return !this.header?.singleSelect
    },
    needsMenu () {
      return this.showSort || this.showFilters
    },
    hasNextPage () {
      return this.filteredHeader?.pageInfo?.hasNextPage ?? false
    }
  },
  watch: {
    activeSort: {
      handler (val) {
        this.selectedSort = val
      },
      immediate: true
    },
    activeFilters: {
      handler (val) {
        if (this.allowMultiSelect) {
          this.selectedFilters = val
        } else {
          this.selectedFilters = val.length > 0 ? val[0] : val
        }
      },
      immediate: true
    },
    'header.filterBy': {
      deep: true,
      immediate: true,
      handler (newOptions) {
        this.filteredHeader = newOptions
      }
    }
  },
  methods: {
    applyChanges () {
      const filters = this.allowMultiSelect ? this.selectedFilters : [this.selectedFilters]
      this.$emit('change', { activeSort: this.selectedSort, activeFilters: filters })
    },
    clearFilters () {
      const options = this.header.filterBy.nodes
      const filters = this.activeFilters
      options.forEach((option) => {
        if (filters.includes(option.value)) {
          filters.splice(filters.indexOf(option.value), 1)
        }
      })
      this.$emit('change', { activeSort: this.selectedSort, activeFilters: filters })
    },
    applySearchOnFilter (values) {
      this.$emit('search-on-filter', values)
    }
  }
}

</script>
<style lang="scss" scoped>
@import '~/assets/css/snui';

.sn-table-column-menu-btn {
  float: right;
  margin: 15px;
}

.sn-table-column-menu {
  min-width: 250px;
}

.sn-table-filter-menu {
  overflow-y: auto;
  max-height: 250px;
}

.sn-table-filter-menu-item {
  color: transparent !important;
}

</style>
